import { UsersAction } from "./../users/users.actions";
import { Injectable, NgZone } from "@angular/core";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppState } from "../../interfaces/app-state.interface";
import { TrackingActions } from "./tracking.action";
import { Router } from "@angular/router";
import { RouterTrackerService } from "../../services/router-tracker/router-tracker.service";
import { of } from "rxjs/observable/of";
import { withLatestFrom, exhaustMap, map, filter, tap } from "rxjs/operators";
import { OrderSentEvent } from "../../models/segment/order-sent-event.model";
import { SegmentEvent } from "../../enum/segment/segment-event.enum";
import { TrackingService } from "../../services/tracking/tracking.service";
import { Observable } from "rxjs/Observable";

declare const ga: any;
const mixpanelEventNameBlockList: String[] = [
    "Loaded a Element",
    "Fixedincome Filter Used",
    "New Search",
    "[Stocks] My order history clicked",
    "Investment not loaded",
    "Investment Closed",
];

const mixpanelLoadedAPageRouteBlockList: String[] = [
    "/bolsa/meus-investimentos",
    "/entrar",
    "/bolsa/explore-o-mercado",
    "/fundos/investir?app=toromobile&categoria=todos",
    "/fundos/investir?app=toromobile",
    "/renda-fixa/meus-investimentos?app=toromobile",
    "/bolsa/oportunidades",
    "/fundos/meus-investimentos?app=toromobile",
    "/renda-fixa/meus-investimentos",
    "/fundos/investir?categoria=todos",
    "/fundos/investir",
    "/minha-conta?app=toromobile",
    "/esqueci-minha-senha",
    "/fundos/meus-investimentos",
    "/minha-conta/dados-pessoais",
    "/minha-conta",
    "/produtos-renda-fixa/investir",
];
@Injectable()
export class TrackingEffects {
    /**
     * Ao enviar uma ordem na página de Checkout
     * envia para o Segment os dados da ordem.
     *
     * type (Planejada ou Nao Planejada)
     * ticker (PETR4)
     * origin (Recomendacao, Notificacao de Recomendacao, Explore o Mercado, Analise)
     * direction (Compra ou Venda)
     * product_type (Bovespa ou BMF)
     */

    @Effect({ dispatch: false })
    public sendOrderInfomation$ = this.actions$.pipe(
        ofType(TrackingActions.sendOrderInformation),
        withLatestFrom(
            this.store.select((state) => state.authentication.currentUser)
        ),
        filter(([action, currentUser]) => action.payload && currentUser),
        tap(([action, currentUser]) => {
            const windowPage: any = window;
            const event = action.payload as OrderSentEvent;

            event["Is Toro App"] = event.isToroApp ? event.isToroApp : false;
            delete event.isToroApp;

            this.tracking.track(SegmentEvent.OrderSent, event);
            this.tracking.alias(currentUser.userId);
        })
    );

    /**
     * Ao fazer login como usuário ativo pela primeira vez
     * envia para o Segment os dados do usuário.
     *
     * cblc (CBLC do usuário)
     */
    @Effect({ dispatch: false })
    public sendFirstLoginActivated$ = this.actions$.pipe(
        ofType(TrackingActions.sendFirstLoginActivated),
        filter((action) => action.payload),
        exhaustMap((action) => {
            return this.ngzone.runOutsideAngular(() => {
                const windowPage: any = window;
                if (windowPage.dataLayer) {
                    windowPage.dataLayer.push({
                        event: "Activated User",
                        order_id: action.payload.cblc,
                    });

                    //TODO(PM's): Removendo temporariamente por causa do excesso de eventos no mixpanel, validar com PM's quando retornar!
                    // this.tracking.track('Activated User', {
                    //     order_id: action.payload.cblc,
                    // });

                    return windowPage.dataLayer;
                }
            });
        })
    );

    /**
     * Ao receber uma mensagem de erro, envia para o segment
     *
     * message (Mensagem de erro)
     */
    @Effect({ dispatch: false })
    public trackErrorMessages$ = this.actions$.pipe(
        ofType(TrackingActions.trackErrorMessages),
        filter((action) => action.payload),
        exhaustMap((action) => {
            this.tracking.track("Processing Error", {
                message: action.payload.message,
            });

            return Observable.of(action);
        })
    );

    /**
     * Atualiza dados das páginas no Segment e Google Tag Manager
     * (Primeiro evento é ignorado para não duplicar com o enviado ao carregar o script)
     */
    @Effect({ dispatch: false })
    public updateDataPage$ = this.actions$.pipe(
        ofType(TrackingActions.updateDataPage),
        withLatestFrom(
            this.store
                .select((state) => !!state.authentication.currentUser)
                .combineLatest(
                    this.store.select((state) => state.userProfile.profile)
                )
        ),
        tap(([action, [isLoggedin, profile]]) => {
            this.ngzone.runOutsideAngular(() => {
                const windowPage: any = window;
                const profileInfo = profile.isTrader
                    ? profile.profileInfo.typeProfile || "Trader"
                    : "NTrader";

                // TODO(PM's): REMOVE POR COMPLETO, TEMPORARIAMENTE, OS EVENTOS DE LOADED A PAGE POR CAUSA DO EXCESSO DOS ENVIOS PARA O MIXPANEL
                // if (mixpanelLoadedAPageRouteBlockList.filter((events) => events.includes(this.router.url)).length === 0) {
                // Evento de page view nativo com analytics
                // this.tracking.track("Loaded a Page", {
                //     title: document.title,
                //     url: `${windowPage.location.origin}${this.router.url}`,
                //     path: this.router.url,
                //     profile: `${profileInfo}`,
                // });
                // }

                // Evento de "page view customizado" com analytics
                if ((window as any).ga) {
                    (window as any).ga("set", "dimension1", "isLoggedin");
                    ga("send", "pageview", { dimension1: isLoggedin });
                }

                // Page view com Tag Manager
                if (
                    windowPage.dataLayer &&
                    this.routerTracker.getHistory().length > 1
                ) {
                    windowPage.dataLayer.push({
                        event: "Page View",
                        url: `${windowPage.location.origin}${this.router.url}`,
                    });
                }
            });
        })
    );

    /**
     * Atualiza dados genéricos no Segment
     *
     * @param events <Object>: Lista de eventos a serem enviadas para o segment, como por exemplo:
     *      { userLogin: '00000000000', element: 'Mais Detalhes' }
     * @param type <String>: Nome do evento a ser enviado para o segment, como por exemplo:
     *      'Interaction'
     */
    @Effect({ dispatch: false })
    public trackEvents$ = this.actions$.pipe(
        ofType(TrackingActions.trackEvents),
        withLatestFrom(
            this.store.select((state) => state.userProfile),
            this.store.select((state) => state.authentication),
            this.store.select((state) => state.suitability)
        ),
        filter(([action]) => action.payload.events && action.payload.type),
        exhaustMap(([action, userProfile, authentication, suitability]) => {
            //TODO(PM's): Removendo temporariamente por causa do excesso de eventos no mixpanel, validar com PM's quando retornar!
            if (mixpanelEventNameBlockList.includes(action.payload.type)) {
                return Observable.of({ ok: true });
            }

            const profileInfo = userProfile.profile.isTrader
                ? userProfile.profile.profileInfo.typeProfile || "Trader"
                : "NTrader";

            action.payload.events["profile"] = profileInfo;

            const events = {
                path: window.location.pathname,
                cblc:
                    authentication.currentUser &&
                    authentication.currentUser.cblc,
                user_id: userProfile.user && userProfile.user.id,
                distinct_id: userProfile.user && userProfile.user.id,
                customer_status: userProfile.user && userProfile.user.profile,
                registration_status:
                    userProfile.user && userProfile.user.registrationStatus,
                suitability_profile:
                    suitability.profile && suitability.profile.description,
                ["Is Toro App"]: false,
                ...action.payload.events,
            };

            const eventWithoutUndefined = Object.entries(events).reduce(
                (agg, [key, value]) => {
                    if (value !== undefined) {
                        return { ...agg, [key]: value };
                    }
                    return agg;
                },
                {}
            );

            this.tracking.track(action.payload.type, eventWithoutUndefined);
            return Observable.of({ ok: true });
        })
    );

    /**
     * Envia dados para o Google Analytics de quando o usuário faz o Cadastro
     */
    @Effect({ dispatch: false })
    public trackFormEvents$ = this.actions$.pipe(
        ofType(TrackingActions.trackFormEvent),
        filter(
            (action) =>
                action.payload.type &&
                action.payload.event &&
                action.payload.event !== this.lastTrackedEvent
        ),
        exhaustMap((action) => {
            this.lastTrackedEvent = action.payload.event;
            if (!(window as any).ga) return of(false);
            (window as any).ga("send", "event", {
                url: `${window.location.origin}${this.router.url}`,
                eventLabel: action.payload.event,
                eventAction: action.payload.type,
                eventCategory: "Cadastro",
            });

            return of(true);
        })
    );

    /**
     * Envia dados para o Segment de quando o usuário faz Login
     */
    @Effect({ dispatch: false })
    public sendTrackLogin$ = this.actions$.pipe(
        ofType(TrackingActions.sendTrackLogin),
        withLatestFrom(
            this.store
                .select((state) => state.authentication.isUserLogin)
                .combineLatest(
                    this.store.select((state) => state.userProfile.profile)
                )
        ),
        filter(([action, [user, profile]]) => action.payload.userId),
        map(([action, [user, profile]]) => {
            if (user) {
                const profileInfo = profile.isTrader
                    ? profile.profileInfo.typeProfile || "Trader"
                    : "NTrader";

                this.tracking.identify(action.payload.userId);
                this.tracking.peopleSet(action.payload.events);

                //TODO(PM's): Removendo temporariamente por causa do excesso de eventos no mixpanel, validar com PM's quando retornar!
                //  this.tracking.track('Login', {
                //      userId: action.payload.userId,
                //      platform: 'Hub',
                //      profile: `${profileInfo}`,

                // });

                this.store.dispatch({
                    type: UsersAction.isUserLogin,
                    payload: false,
                });
            }
        })
    );

    /**
     * Envia dados para o Google Analytics de quando o usuário faz Login
     */
    @Effect({ dispatch: false })
    public trackLoginEvents$ = this.actions$.pipe(
        ofType(TrackingActions.trackLoginEvent),
        filter(
            (action) =>
                action.payload.type &&
                action.payload.event &&
                action.payload.event !== this.lastTrackedEvent
        ),
        exhaustMap((action) => {
            this.lastTrackedEvent = action.payload.event;
            if (!(window as any).ga) return of(false);
            (window as any).ga("send", "event", {
                url: `${window.location.origin}${this.router.url}`,
                eventLabel: action.payload.event,
                eventAction: action.payload.type,
                eventCategory: "Login",
            });

            return of(true);
        })
    );

    /**
     * Envia dados para o Google Analytics.
     *
     * @param url: string = a url em que o usuário se encontra e onde o GA será acionado
     * @param eventLabel: string = valor a ser enviado para o GA.
     * @param eventAction: string = valor que informa ao GA qual evento foi trackeado. Ex: Search, Filter ou Click.
     * @param eventCategory: string = normalmente é a página onde o GA será acionado. Ex: Login, Fixed Income, Cadastro.
     * @param eventValue: string = Valor a ser informado para o GA referente a página.
     */
    @Effect({ dispatch: false })
    public trackingGAEvents$ = this.actions$.pipe(
        ofType(TrackingActions.trackEventsToGA),
        filter(
            (action) =>
                action.payload.type &&
                action.payload.event &&
                action.payload.event !== this.lastTrackedEvent
        ),
        exhaustMap((action) => {
            this.lastTrackedEvent = action.payload.event;
            const gaObject = {
                url: `${window.location.origin}${this.router.url}`,
                eventLabel: action.payload.event,
                eventAction: action.payload.type,
                eventCategory: action.payload.category,
            };
            const value = action.payload.value;
            if (value) {
                // tslint:disable-next-line: no-string-literal
                gaObject["eventValue"] = action.payload.value;
            }
            ga("send", "event", gaObject);
            return of(true);
        })
    );

    private lastTrackedEvent = "";

    constructor(
        private actions$: Actions<TrackingActions>,
        private store: Store<AppState>,
        private ngzone: NgZone,
        private router: Router,
        private routerTracker: RouterTrackerService,
        private tracking: TrackingService
    ) { }
}
